/**
 * AnnotationsList Component
 * 
 * Displays a filterable timeline of session flag annotations with review and management tools.
 * Designed for proctors and reviewers to efficiently review and take action on flagged events
 * during exam session playback.
 * 
 * Features:
 * - Timeline visualization of flag annotations with severity indicators
 * - Bulk edit mode for batch approval/deletion of flags
 * - Reviewer tools for annotation management
 * - Auto-scroll to follow current playback position
 * - Filter for unreviewed annotations
 * - Keyboard shortcuts for common actions
 * - Permission-based access control for sensitive operations
 */

import React, { useEffect, useState, useRef, useCallback, useId, useMemo } from 'react';
import './styles.css';
import 'react-virtualized/styles.css';
import { View, Link, Alert } from '@smarterservices/smarter-ui';
import { List, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import NoFlags from './NoFlags';
import Loader from '@ui/context/session/components/Common/Loader';
import { useExamSessionStore, usePlayerStore, getStore } from '../../../stores/StoreContext';
import AnnotationControls from './AnnotationControls';
import BulkEditControls from './BulkEditControls';
import AnnotationTimelineItem from './AnnotationTimelineItem';
import useAnnotationKeyboardShortcuts from './hooks/useAnnotationKeyboardShortcuts';
import useAnnotationTimeline from './hooks/useAnnotationTimeline.jsx';
import useBulkAnnotationActions from './hooks/useBulkAnnotationActions';

/**
 * AnnotationsList Component
 * Main component for displaying and managing flag annotations
 */
const AnnotationsList = () => {
  // Constants
  const menuHeight = 55;
  const types = ['flags'];

  // State: UI Controls
  const [showReviewerTools, setShowReviewerTools] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [showUnreviewed, setShowUnreviewed] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);
  const [autoscrollTopOffset, setAutoscrollTopOffset] = useState(menuHeight);

  // State: Bulk Edit
  const [bulkEdit, setBulkEdit] = useState(false);
  const [bulkChangeInProcess, setBulkChangeInProcess] = useState(false);
  const [bulkChangeType, setBulkChangeType] = useState(null);
  const [bulkSelectedItems, setBulkSelectedItems] = useState([]);
  const [bulkChangePercentage, setBulkChangePercentage] = useState(0);
  const [bulkChangeCompleted, setBulkChangeCompleted] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkSelectedItemsCompleted, setBulkSelectedItemsCompleted] = useState([]);


  // Refs
  const timelineRef = useRef();
  const controlsRef = useRef();
  const listRef = useRef();

  // CellMeasurer cache for dynamic row heights
  const cacheRef = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 150, // Estimated default height
    })
  );

  // Store instances
  const ExamSessionStore = getStore('examSessionStore');
  const PlayerStore = getStore('playerStore');
  const AppStore = getStore('appStore');

  // Store State Subscriptions
  const annotations = useExamSessionStore((state) => state.annotations);

  // Generate unique base ID for timeline items
  const baseId = useId();

  // Memoized handlers
  const handleToggleReviewerTools = useCallback(() => {
    setShowReviewerTools(prev => {
      const newValue = !prev;
      if (newValue) {
        setShowDetails(true);
      }
      return newValue;
    });
  }, []);

  const handleToggleDetails = useCallback(() => {
    setShowDetails(prev => !prev);
  }, []);

  const handleUnreviewedToggle = useCallback(() => {
    setShowUnreviewed(prev => !prev);
  }, []);

  const handleAutoScrollToggle = useCallback(() => {
    setAutoScrollEnabled(prev => !prev);
  }, []);

  const handleBulkEditToggle = useCallback(() => {
    setBulkEdit(prev => !prev);
  }, []);

  // Keyboard shortcuts
  useAnnotationKeyboardShortcuts({
    onUnreviewedToggle: handleUnreviewedToggle,
    onDetailsToggle: handleToggleDetails,
    onReviewerToolsToggle: handleToggleReviewerTools,
    onBulkEditToggle: handleBulkEditToggle,
    onAutoScrollToggle: handleAutoScrollToggle,
  });

  // Timeline data hook - must be called before callbacks that use timelineData
  const { timelineData, initialized } = useAnnotationTimeline({
    annotations,
    bulkEdit,
    bulkSelectedItems,
    showUnreviewed,
    showReviewerTools,
    showDetails,
    ExamSessionStore,
  });

  // Handle bulk selection changes
  const handleBulkSelectionChange = useCallback((annotationId) => {
    setBulkSelectedItems(prev => {
      let newSelectedItems;
      if (prev.includes(annotationId)) {
        newSelectedItems = prev.filter(id => id !== annotationId);
      } else {
        newSelectedItems = [...prev, annotationId];
      }

      // Check if ALL visible items are selected (not just count match)
      const allVisibleSelected = timelineData.length > 0 &&
        timelineData.every(item => newSelectedItems.includes(item.annotation.sid));
      setSelectAll(allVisibleSelected);

      return newSelectedItems;
    });
  }, [timelineData]);

  const handleSelectAll = useCallback(() => {
    // Select only the visible/filtered annotations from timelineData
    const visibleAnnotationIds = timelineData.map(item => item.annotation.sid);
    setBulkSelectedItems(visibleAnnotationIds);
    setSelectAll(true);
  }, [timelineData]);

  const handleDeselectAll = useCallback(() => {
    setBulkSelectedItems([]);
    setSelectAll(false);
  }, []);

  // Clear selections when bulk edit is disabled and update autoscroll offset
  useEffect(() => {
    if (!bulkEdit) {
      setBulkSelectedItems([]);
      setSelectAll(false);
    }

    const updateOffset = () => {
      if (controlsRef.current) {
        const rect = controlsRef.current.getBoundingClientRect();
        if (rect.height > 0) {
          setAutoscrollTopOffset(rect.height + 5);
        }
      }
    };

    const timeoutId = setTimeout(updateOffset, 100);
    return () => clearTimeout(timeoutId);
  }, [bulkEdit]);

  // Clear cache and recompute row heights when annotations are deleted or data changes
  useEffect(() => {
    if (cacheRef.current && listRef.current) {
      cacheRef.current.clearAll();
      listRef.current.recomputeRowHeights();
    }
  }, [timelineData.length]);

  // Callback to remeasure a specific row when its height changes
  const handleRowHeightChange = useCallback((index) => {
    if (cacheRef.current && listRef.current) {
      cacheRef.current.clear(index, 0);
      listRef.current.recomputeRowHeights(index);
    }
  }, []);

  // Memoize row renderer to prevent unnecessary re-renders
  const rowRenderer = useCallback(({ index, key, parent, style }) => {
    const item = timelineData[index];
    return (
      <CellMeasurer
        key={key}
        cache={cacheRef.current}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        <div style={style}>
          <AnnotationTimelineItem
            key={`annotation_item${item.annotation.sid}`}
            annotation={item.annotation}
            index={index}
            totalItems={timelineData.length}
            isHighlighted={false}
            baseId={baseId}
            bulkEdit={item._meta.bulkEdit}
            isSelected={item._meta.isSelected}
            showReviewerTools={item._meta.showReviewerTools}
            showDetails={item._meta.showDetails}
            PlayerStore={PlayerStore}
            onBulkSelectionChange={handleBulkSelectionChange}
            onHeightChange={() => handleRowHeightChange(index)}
          />
        </div>
      </CellMeasurer>
    );
  }, [timelineData, baseId, handleBulkSelectionChange, handleRowHeightChange, PlayerStore]);

  // Memoize the entire virtualized list to prevent re-rendering on parent state changes
  const virtualizedList = useMemo(() => (
    <View as="div" minHeight="100%" width="100%" height="calc(100vh - 200px)">
      <AutoSizer>
        {({ height, width }) => (
          <List
            ref={listRef}
            width={width}
            height={height}
            rowCount={timelineData.length}
            deferredMeasurementCache={cacheRef.current}
            rowHeight={cacheRef.current.rowHeight}
            overscanRowCount={5}
            rowRenderer={rowRenderer}
          />
        )}
      </AutoSizer>
    </View>
  ), [timelineData.length, rowRenderer]);

  // Bulk actions hook
  const { handleBulkApprove, handleBulkDelete } = useBulkAnnotationActions({
    bulkSelectedItems,
    ExamSessionStore,
    AppStore,
    setBulkChangeType,
    setBulkChangeInProcess,
    setBulkSelectedItemsCompleted,
    setBulkChangePercentage,
    setBulkEdit,
    setBulkChangeCompleted,
    setBulkSelectedItems,
    setSelectAll,
  });

  // Show loader while initializing
  if (!initialized) {
    return (
      <View as="div" margin="medium 0 0 0">
        <Loader
          spinnerSize="small"
          color="primary"
          textSize="medium"
          text='Loading session flags'
        />
      </View>
    );
  }

  return (
    <View as="div" position="relative">
      {timelineData.length ? (
        <>
          <AnnotationControls
            showUnreviewed={showUnreviewed}
            showReviewerTools={showReviewerTools}
            showDetails={showDetails}
            bulkEdit={bulkEdit}
            autoScrollEnabled={autoScrollEnabled}
            onUnreviewedToggle={handleUnreviewedToggle}
            onReviewerToolsToggle={handleToggleReviewerTools}
            onDetailsToggle={handleToggleDetails}
            onBulkEditToggle={handleBulkEditToggle}
            onAutoScrollToggle={handleAutoScrollToggle}
            sessionSid={ExamSessionStore.sessionSid}
            getIamResourceString={AppStore.getIamResourceString}
            controlsRef={controlsRef}
          />

          <BulkEditControls
            bulkEdit={bulkEdit}
            bulkChangeInProcess={bulkChangeInProcess}
            bulkChangePercentage={bulkChangePercentage}
            bulkChangeType={bulkChangeType}
            bulkChangeCompleted={bulkChangeCompleted}
            bulkSelectedItems={bulkSelectedItems}
            totalAnnotations={ExamSessionStore.getAnnotations({ types }).length}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBulkApprove={handleBulkApprove}
            onBulkDelete={handleBulkDelete}
            onExitBulkMode={() => setBulkEdit(false)}
          />

          {/* Render annotations with react-virtualized for performance */}
          {/* Memoized to prevent re-rendering when parent re-renders (collapse/expand, mode switching) */}
          {virtualizedList}
        </>
      ) : (
        <>
          {showUnreviewed ? (
            <Alert variant="info" transition="none">
              There are no unreviewed flags associated with this session.
              <Link onClick={handleUnreviewedToggle}>Remove the filter</Link> to show all annotations.
            </Alert>
          ) : (
            <NoFlags />
          )}
        </>
      )}
    </View>
  );
};

export default AnnotationsList;
